import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

// Every factual claim maps to docs/ci-research-chatgpt-ads-no-impressions/FINDINGS-2026-10-17.md (F1-F28).
// Screenshots are first-hand captures from our own Ads Manager account, 2026-10-17 (read-only; nothing saved).

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const BOT_CHECKER = "/tools/ai-bot-checker";
const SHOTS = "/screenshots/chatgpt-ads-manager";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: 19,
        color: TEXT,
        margin: "28px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontSize: 16, lineHeight: 1.75, color: TEXT_DIM, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 10 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}44` }}
      {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        background: "rgba(79,205,196,0.05)",
        borderRadius: "0 12px 12px 0",
        fontFamily: "var(--display)",
        fontSize: 19,
        lineHeight: 1.5,
        color: TEXT,
        fontWeight: 500,
      }}
    >
      {children}
    </blockquote>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "36px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: "rgba(79,205,196,0.06)",
        border: "1px solid rgba(79,205,196,0.2)",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 14 }}>{children}</div>
      <a
        href={href}
        style={{
          display: "inline-block",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          minHeight: 44,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

const th = (accent) => ({
  textAlign: "left",
  padding: "12px 14px",
  color: accent ? TEAL : TEXT,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontFamily: "var(--mono)",
});
const td = (strong) => ({
  padding: "13px 14px",
  color: strong ? TEXT : TEXT_DIM,
  fontWeight: strong ? 600 : 400,
  verticalAlign: "top",
});

function DataTable({ title, note, headers, rows, minWidth = 680 }) {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>{title}</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>{note}</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
              {headers.map((h, i) => (
                <th key={h} scope="col" style={th(i === 0)}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row[0]} style={{ borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                {row.map((cell, j) => (
                  <td key={j} style={td(j === 0)}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const timingRows = [
  ["First delivery after launch", "Allow at least 24 hours when nothing is visibly blocked", "Leave settings alone on day one unless a status shows a blocker"],
  ["Impressions, clicks and CTR", "Refresh about every 15 minutes once ads are delivering", "A zero here after a full day is real"],
  ["Spend", "Delayed about 7 to 8 hours", "Impressions with zero spend are normal for a while"],
  ["Average CPC and CPM", "Update after spend settles", "Read them the next day, not the same hour"],
  ["Low bid warning", "Not raised within 24 hours of an ad group change, per OpenAI's developer docs", "No warning yet does not mean the bid is competitive"],
  ["Conversions", "Can take 24 to 48 hours to appear", "Zero conversions is a different problem from zero impressions"],
];

const levels = [
  {
    n: "1",
    name: "Account",
    blocks: [
      "Verification or billing not finished, or the payment method is invalid",
      "Brand or legal name changed and back in review",
      "An account spend limit used up",
      "A new account still limited to its home country",
    ],
    look: "Settings (General) and Billing. If nothing in the account serves, start here.",
  },
  {
    n: "2",
    name: "Campaign",
    blocks: [
      "Switched off, or today is outside its start and end dates",
      "The weekly budget allowance already spent",
      "No country it is allowed to serve in",
      "Targeting that leaves almost nobody eligible",
    ],
    look: "Campaigns tab, Status column, then the campaign's settings.",
  },
  {
    n: "3",
    name: "Ad group",
    blocks: [
      "Switched off, or it has no ads",
      "A bid too low to win (the meter reads May not deliver)",
      "A Conversions bid set as if it were a click bid",
    ],
    look: "Ad groups tab, then Edit Ad Group and Bid strategy.",
  },
  {
    n: "4",
    name: "Ad",
    blocks: [
      "Switched off, or still in review",
      "Rejected under OpenAI's Ad Policies",
      "A landing page OpenAI's crawler cannot reach",
    ],
    look: "Ads tab. Hover over Not serving for the reason.",
  },
];

function FourLevelCheck() {
  return (
    <div style={{ margin: "16px 0 32px", padding: 24, borderRadius: 14, background: "#0f0f11", border: `1px solid ${CARD_BORDER}` }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The four-level check for a ChatGPT Ads campaign with no impressions</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 18, fontFamily: "var(--body)" }}>Work top to bottom. A block at any level stops everything beneath it.</div>
      <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
        {levels.map((l) => (
          <li
            key={l.n}
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              padding: "18px 20px",
              borderRadius: 12,
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "rgba(79,205,196,0.12)",
                    color: TEAL,
                    fontFamily: "var(--mono)",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {l.n}
                </span>
                <span style={{ fontFamily: "var(--display)", fontSize: 17, fontWeight: 700, color: TEXT }}>{l.name}</span>
              </div>
              <div style={{ fontSize: 12, fontFamily: "var(--mono)", color: TEAL, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>Where to look</div>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: TEXT_DIM }}>{l.look}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontFamily: "var(--mono)", color: TEAL, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 6 }}>Blocks delivery when</div>
              <ul style={{ paddingLeft: 18, margin: 0, listStyleType: "disc" }}>
                {l.blocks.map((b) => (
                  <li key={b} style={{ fontSize: 14, lineHeight: 1.6, color: TEXT_DIM, marginBottom: 4 }}>{b}</li>
                ))}
              </ul>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

const faqs = [
  {
    q: "Why are my ChatGPT Ads not showing?",
    a: "Either something is blocking delivery or the ad cannot win enough auctions. Check four levels in order: the account (verification, billing, brand review, spend limits), the campaign (status, dates, weekly budget, countries), the ad group (bid strength, bid strategy) and the ad (review, landing page access). In Ads Manager, hover over Not serving to see every reason. If nothing is blocked, your targeting or bid is likely leaving too little to win.",
  },
  {
    q: "Why can't I see my own ChatGPT ad?",
    a: "OpenAI does not show ads to users on Plus, Pro or any Business plan, or to accounts identified as belonging to users under 18. If you check from a paid ChatGPT account, you will never see your own ad, however well it is running. Ads also stay out of conversations OpenAI treats as sensitive. Use the Impressions column in Ads Manager, not your own chats, to judge delivery.",
  },
  {
    q: "How long does ChatGPT Ads review take?",
    a: "OpenAI does not publish a standard review time. Ads can start delivering once they pass review and the account, billing and campaign requirements are also met. Every edit to an ad's creative triggers another review. If an ad is rejected and you think it was a mistake, use Request Review or the appeal option. The 7 days you may see quoted is not a review time: it is OpenAI's point for contacting Ads Support about an appeal that is still pending.",
  },
  {
    q: "Will raising my bid fix zero impressions?",
    a: "Only if the bid is the problem. OpenAI's documentation says increasing a bid will not resolve a paused campaign, an exhausted account limit or a rejected ad, and that raising a bid does not guarantee delivery. Check for blockers first. If none exist and the bid meter reads May not deliver, a higher bid is a reasonable next test; OpenAI suggests starting CPC bids at $3 to $5 in US dollars.",
  },
  {
    q: "What does 'no eligible targeting country' mean in ChatGPT Ads?",
    a: "Your selected locations do not include any country where your account can currently advertise. Some new self-serve accounts can only advertise in their home country until identity verification is approved and they reach a required level of spend there, and selecting other countries does not override that. Some regulated business categories are also limited to certain countries.",
  },
  {
    q: "Why did my ChatGPT Ads stop after I changed my brand name?",
    a: "Changing the public brand name or legal name on your ad account sends the account back into review, and OpenAI's developer documentation says the change pauses ad delivery until the account passes that review. Avoid renaming mid-campaign if you can.",
  },
  {
    q: "Why did my ChatGPT Ads stop delivering late in the week?",
    a: "A daily budget in ChatGPT Ads is an average over a Sunday-to-Saturday week, not a fixed daily cap. A campaign can spend up to twice its daily budget in one day and up to seven times it across the week, so a campaign that spends heavily early in the week can run out of allowance before Saturday. OpenAI's troubleshooting guide calls this out directly.",
  },
];

const howToSteps = [
  ["Wait 24 hours unless something is visibly blocked", "For a new campaign with no clear blocker, allow at least 24 hours after launch before treating zero impressions as a problem. Act at once on a hard error or a billing or review blocker."],
  ["Read every Not serving reason", "In Ads Manager, hover over Not serving on the campaign, ad group and ad, and page through every reason the tooltip lists."],
  ["Check the account", "Confirm verification and billing are complete, the account is not back in review after a name change, any account spend limit has room left, and the account can advertise in your target countries."],
  ["Check the campaign", "Confirm it is switched on, today is inside its dates, the weekly budget allowance is not used up, and its countries and audiences leave an eligible population."],
  ["Check the ad group", "Confirm it is switched on and has ads, read the bid strength meter, and make sure a Conversions bid reflects the conversion rather than a click."],
  ["Check the ad", "Confirm review has finished, fix or appeal any rejection, and make sure OpenAI's OAI-AdsBot crawler can reach the landing page."],
  ["Widen what can compete, one change at a time", "If nothing is blocked, loosen targeting or raise the bid one change at a time, note each change, and compare results over a full day or more."],
  ["Contact OpenAI support", "Email ads-support@openai.com with the account name, campaign name, launch date and a screenshot showing zero delivery."],
];

function Content() {
  return (
    <article>
      {/* ── Overview box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          When a ChatGPT Ads campaign gets no impressions, either something is blocking delivery at one of four levels (the account, the campaign, the ad group or the ad), or nothing is blocked and the ad cannot win enough auctions. Ads Manager tells you about the first kind. The second you have to work out.
        </P>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li><Strong>Wait first:</Strong> OpenAI says to allow at least 24 hours after launch before treating zero delivery as a problem</Li>
          <Li><Strong>Start here:</Strong> hover over Not serving in Ads Manager and page through every reason it lists</Li>
          <Li><Strong>Often missed:</Strong> nobody on a Plus, Pro or Business plan ever sees ads, including you</Li>
          <Li><Strong>Bids:</Strong> raising a bid never fixes a paused campaign, a used-up limit or a rejected ad</Li>
          <Li><Strong>Landing pages:</Strong> OpenAI&#39;s ad crawler has to be able to reach them</Li>
        </ul>
      </div>

      <H2 id="is-it-broken">Is it actually broken? What is normal in the first 24 hours</H2>
      <P>
        Give a new campaign a day before you change anything. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001217-troubleshooting-common-issues">troubleshooting guide</A> says that for a newly launched campaign with no confirmed delivery and no clear blocker, you should &ldquo;allow at least 24 hours after launch&rdquo; before reporting a zero-delivery issue. The exception is a hard error, or a clear billing or review blocker. For those, OpenAI says to contact support without waiting.
      </P>
      <P>
        Reporting also runs on several clocks, so a quiet dashboard is not always a quiet campaign:
      </P>
      <DataTable
        title="What is normal timing in ChatGPT Ads Manager"
        note="OpenAI's figures, checked October 17, 2026. OpenAI describes them as approximate, not guarantees."
        headers={["Signal", "What is normal", "What it means for you"]}
        rows={timingRows}
      />
      <P>
        If impressions are arriving but conversions are not, you have a tracking problem, not a delivery problem. Our guide on <A href="/blog/how-to-check-chatgpt-ads-pixel-is-working">checking that your pixel works</A> covers that.
      </P>

      <H3>You will not see your own ad on a paid ChatGPT plan</H3>
      <P>
        The other false alarm is searching for your ad in ChatGPT yourself. OpenAI&#39;s Help Center says it does <A href="https://help.openai.com/en/articles/20001207-ads-in-chatgpt-the-basics">not show ads</A> to users on Plus, Pro or any Business plan, or to accounts identified as belonging to users under 18. If your team runs on ChatGPT Business, nobody on it will ever see your ad, however well it is running.
      </P>
      <P>
        Ads also stay out of conversations OpenAI&#39;s safeguards treat as sensitive or unsuitable, and <A href="/glossary/context-hint">context hints</A> guide matching without guaranteeing your ad appears in any particular conversation. Typing your target question into ChatGPT is not a delivery test. The Impressions column is.
      </P>

      <H2 id="four-levels">Check four levels, in order</H2>
      <P>
        Delivery runs through a chain: the ad account, the campaign, the ad group and the ad. OpenAI&#39;s <A href="https://developers.openai.com/ads/troubleshooting">developer documentation</A> puts it plainly: &ldquo;An ad&#39;s status alone does not describe the entire hierarchy.&rdquo; An ad that is approved and switched on still cannot serve inside a paused campaign.
      </P>
      <FourLevelCheck />
      <P>
        In Ads Manager, the Status column does the first pass for you. Anything that is delivering shows Serving. Anything else shows Not serving, and hovering over it shows why.
      </P>
      <ScreenshotFrame
        src={`${SHOTS}/chatgpt-ads-manager-campaigns-not-serving-status.png`}
        title="Ads Manager / Campaigns"
        alt="OpenAI's ChatGPT Ads Manager campaigns list with four paused test campaigns, each switched off and marked Not serving in the Status column."
        caption="Our own account: four paused test campaigns. A +2 next to Not serving means more reasons are waiting in the tooltip."
      />
      <P>
        What we found in our own account is that the tooltip lists every reason it knows about, across the whole chain, one page at a time. One paused test ad group carried three:
      </P>
      <ScreenshotFrame
        src={`${SHOTS}/chatgpt-ads-manager-not-serving-reasons-stacked.png`}
        title="Ads Manager / Ad groups / Status"
        chip="Three pages of one tooltip"
        alt="Three pages of the Not serving tooltip in OpenAI's ChatGPT Ads Manager: Campaign is disabled, Ad group is disabled, and Ad group has no Ads."
        caption="One ad group, three reasons. Switching the campaign back on would not have started delivery on its own."
      />
      <P>
        Read every page before you change anything, and fix every reason, not just the first. OpenAI also keeps the approval checks apart: account verification, account approval, ad review and ad delivery are, in its words, &ldquo;separate checks,&rdquo; and completing one does not complete the others.
      </P>

      <H2 id="account">Account: verification, billing, brand review and spend limits</H2>
      <P>
        Account problems stop every campaign at once. If nothing in the account is serving, start here.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Verification and billing.</Strong> OpenAI lists unfinished account verification, an incomplete billing setup and an invalid payment method among the first things to check.</Li>
        <Li><Strong>Brand review.</Strong> Changing your account&#39;s public brand name or legal name sends it back into review, and OpenAI&#39;s developer documentation says the change &ldquo;pauses ad delivery until the account passes review.&rdquo; Tidying the name in Settings mid-campaign can stop every ad you run.</Li>
        <Li><Strong>Account spend limits.</Strong> Some accounts can cap total spend across all campaigns for a date range. A campaign can still have budget left while the account&#39;s limit is used up. OpenAI notes these limits are available only to some accounts.</Li>
        <Li><Strong>The home-country rule.</Strong> Some new self-serve accounts can advertise only in the country the account belongs to, until identity verification is approved and they reach a required level of spend there. In OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001210-create-campaigns-for-chatgpt-ads">campaign guide</A>, &ldquo;Selecting other countries does not override this restriction.&rdquo;</Li>
      </ul>

      <H2 id="campaign">Campaign: status, dates, budget and targeting</H2>
      <P>
        The campaign has to be switched on, and today has to fall between its start and end dates. One trap catches first-time advertisers: on your first campaign, the start and end date options may not appear until after it is created. OpenAI&#39;s advice is not to publish just to reach them, because the ads could start running before you have set a schedule.
      </P>
      <H3>Budget: a weekly allowance, not a daily cap</H3>
      <P>
        A <A href="https://help.openai.com/en/articles/20001413-daily-budgets">daily budget</A> in ChatGPT Ads is the average you want to spend per day over a seven-day period. A campaign can spend up to twice its daily budget in one day and up to seven times it in the Sunday-to-Saturday week, in your account&#39;s time zone. OpenAI&#39;s troubleshooting guide warns that &ldquo;using the weekly allowance early can stop delivery later in the week.&rdquo; A campaign that delivered on Monday and went quiet on Thursday may simply be out of allowance until the new week starts.
      </P>
      <P>
        Campaign total budgets pace differently: if a campaign is ahead of pace, delivery may slow to save budget for later, and an end date does not guarantee the budget lasts until then. For what bids and budgets cost in practice, see our <A href="/blog/chatgpt-ads-cost">ChatGPT Ads cost guide</A>.
      </P>
      <H3>Targeting: countries, platforms and audiences together</H3>
      <P>
        If Ads Manager says there is no eligible targeting country, your selected locations include no country where the account can currently advertise, usually because of the home-country rule above. Your business category matters too. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001534-troubleshooting-common-onboarding-and-policy-issues">policy eligibility table</A> limits several health and financial categories by country, and an ad &ldquo;can pass review and still be ineligible to serve in its selected countries.&rdquo;
      </P>
      <P>
        OpenAI&#39;s developer documentation asks you to inspect geography, platform, included audiences and exclusions together, because each one narrows the pool. Three points matter most for B2B:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>Platform targeting can limit delivery to any mix of Android app, Android web, Desktop web, iOS app and iOS web.</Li>
        <Li>Including a <A href="/glossary/custom-audiences">custom audience</A> requires at least 25,000 matched users. Excluding one has no such floor, which is why our <A href="/blog/how-to-set-up-chatgpt-ads-custom-audiences">custom audiences guide</A> starts with exclusions.</Li>
        <Li>In OpenAI&#39;s words, &ldquo;A large audience upload can still leave a small eligible population after matching and exclusions.&rdquo; Stacked exclusions on a narrow country list can leave very few people to show an ad to.</Li>
      </ul>

      <H2 id="ad-group">Ad group: bids that cannot win</H2>
      <P>
        An ad group has to be switched on and contain at least one ad. Then comes the bid. When you edit an ad group, Ads Manager shows a bid strength meter next to a manual bid. Ours read &ldquo;May not deliver&rdquo; at a $2.00 maximum cost per click:
      </P>
      <ScreenshotFrame
        src={`${SHOTS}/chatgpt-ads-manager-bid-strength-may-not-deliver.png`}
        title="Ads Manager / Edit ad group / Bid strategy"
        alt="OpenAI's ChatGPT Ads Manager bid strategy panel with Autobid and Manual Max CPC options, a $2.00 maximum CPC bid and a May not deliver warning."
        caption="A test ad group in a Clicks campaign. We closed the panel without saving."
      />
      <P>
        OpenAI&#39;s Help Center suggests starting CPC bids at $3 to $5 per click, in US dollars. Treat the meter as guidance, not a verdict. OpenAI says bid recommendations are estimates, while its separate Low bid warning uses observed auction performance, so the two can differ, and that &ldquo;raising your bid does not guarantee delivery.&rdquo; Its developer documentation adds that the Low bid warning is not raised for an ad group that is switched off, or within 24 hours of a change to it, so no warning on day one proves nothing.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Conversions campaigns.</Strong> OpenAI&#39;s troubleshooting guide says to make sure your bid &ldquo;reflects the selected conversion rather than a click.&rdquo; Size it from what a conversion is worth to you. Our <A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">Conversions campaign guide</A> shows how to set one from your own numbers.</Li>
        <Li><Strong>Autobid.</Strong> The Help Center calls it Maximize results, and it is selected by default for eligible new ad groups. It adjusts bids to get the most results from your budget and does not take a maximum bid, so check that the daily budget and campaign objective suit it. Our post on <A href="/blog/chatgpt-ads-maximize-results-b2b">when B2B should opt out of Autobid</A> covers the trade-off.</Li>
        <Li><Strong>Context hints.</Strong> Hints describe where your ad is relevant. OpenAI says they do not guarantee your ad appears in particular conversations and cannot enforce geography, schedules or exclusions. Our guide to <A href="/blog/what-are-context-hints-in-chatgpt-ads">writing context hints</A> covers how to write them well.</Li>
      </ul>

      <H2 id="ad-review">Ad: review, rejections and landing pages OpenAI cannot reach</H2>
      <P>
        An ad that is switched on can still be waiting for review. OpenAI&#39;s developer documentation describes an ad that is active and still in review as &ldquo;enabled but has not completed review.&rdquo; Two details catch people out:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>Editing an ad&#39;s creative creates a new version and triggers another review, so tweaking a live ad sends it back to the queue.</Li>
        <Li>OpenAI does not publish a standard review time. For a rejection you think is wrong, use Request Review or the appeal option. OpenAI says to contact support only if no appeal option is available or an appeal has been pending for more than 7 days.</Li>
      </ul>
      <P>
        OpenAI names two common rejection causes: ad copy or landing pages that do not follow its <A href="https://openai.com/policies/ad-policies/">Ad Policies</A>, and landing pages that block OpenAI&#39;s user agents or IP addresses. The second is easy to miss, because your page loads fine for you.
      </P>
      <H3>Make sure OAI-AdsBot can reach your landing page</H3>
      <P>
        When you submit an ad, OpenAI may visit the landing page with a crawler to check it against its policies, and may use the page to judge when the ad is relevant. Its <A href="https://help.openai.com/en/articles/20001243-advertiser-guidance-for-allowing-openai-web-crawlers">crawler guidance</A> says, &ldquo;You must allow OAI-AdsBot,&rdquo; and recommends allowing OAI-SearchBot too. It names three layers that can block <A href="/glossary/ai-crawlers">AI crawlers</A>:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>robots.txt</Strong> rules that disallow the crawler. It stops immediately.</Li>
        <Li><Strong>Bot protection</Strong> from services such as Cloudflare or Akamai, which can return 403 errors, and rate limiting, which shows up as 429 errors.</Li>
        <Li><Strong>Human checks</Strong> such as CAPTCHAs and JavaScript challenges.</Li>
      </ul>
      <P>
        If your security team needs fixed addresses, OpenAI publishes them at openai.com/adsbot.json. Our explainer on <A href="/blog/what-is-oai-adsbot">what OAI-AdsBot is</A> covers the crawler in more depth, and our guide to <A href="/blog/how-to-optimize-landing-pages-for-chatgpt-ads">optimizing landing pages for ChatGPT Ads</A> covers what the page should say once the crawler gets in.
      </P>
      <InlineCTA href={BOT_CHECKER} label="Check your robots.txt for free">
        Our free AI Bot Access Checker reads your robots.txt and tells you whether OAI-AdsBot and other AI crawlers are allowed. It checks robots.txt only, so firewall and CAPTCHA blocks still need your team.
      </InlineCTA>

      <H2 id="nothing-blocked">Nothing is blocked, but nothing is serving</H2>
      <P>
        If every status is clean and a full day has passed, the problem is competition, not a fault. OpenAI&#39;s developer documentation says an empty list of serving issues &ldquo;is not a promise of impressions,&rdquo; and names the usual reasons: limited eligible opportunities, restrictive targeting, uncompetitive bids or a small usable product set.
      </P>
      <PullQuote>
        An approved ad with nothing blocking it is not a promise of impressions. It is permission to compete.
      </PullQuote>
      <P>
        The pool you compete for is smaller than ChatGPT&#39;s whole audience. Paid-plan users and under-18 accounts never see ads, and sensitive conversations are excluded. Among the ads that are eligible, OpenAI&#39;s system weighs the conversation, your landing page, title, copy and hints, and chooses with a relevance-weighted, second-price auction. A B2B campaign can shrink that pool further in ways that are each reasonable on their own: one country, a few platforms, several exclusion lists and hints written for a narrow niche.
      </P>
      <P>
        Widen deliberately. OpenAI&#39;s advice is to record what changed and when, compare the same reporting scope and time zone over a suitable interval, and avoid changing several settings at once if you want to know which change worked. Ads Manager keeps the record for you under Tools, in Change History:
      </P>
      <ScreenshotFrame
        src={`${SHOTS}/chatgpt-ads-manager-change-history-log.png`}
        title="Ads Manager / Tools / Change History"
        alt="OpenAI's ChatGPT Ads Manager Change History log listing ad and ad group creation in our account, with each field's old and new value, the user and the time."
        caption="Every change is logged with its old and new value, who made it and when. Filter by item, user and date."
      />

      <H2 id="mistakes">Mistakes that make zero impressions harder to fix</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Raising the bid first.</Strong> OpenAI&#39;s documentation is blunt: &ldquo;Increasing a bid will not resolve a paused campaign, exhausted account limit, or rejected ad.&rdquo; Rule out blockers before you pay more.</Li>
        <Li><Strong>Changing everything at once.</Strong> If delivery starts, you will not know why, and you may have raised costs you did not need to.</Li>
        <Li><Strong>Re-editing live ads.</Strong> Every creative edit restarts review.</Li>
        <Li><Strong>Renaming the brand mid-campaign.</Strong> A brand or legal name change pauses delivery until the account passes review.</Li>
        <Li><Strong>Judging too early.</Strong> Impressions refresh about every 15 minutes and spend lags about 7 to 8 hours. Judge over a full day or more.</Li>
        <Li><Strong>Checking ChatGPT yourself.</Strong> On a paid plan you will never see your ad.</Li>
      </ul>
      <PullQuote>
        Hover first, change one thing, then wait a day. Most zero-impression problems are found in the tooltip, not the bid.
      </PullQuote>

      <H2 id="contact-support">When to contact OpenAI support, and what to send</H2>
      <P>
        Contact support after 24 hours of zero impressions with no blocker you can find, or straight away for a hard error, a billing problem or a review blocker. The address is <A href="mailto:ads-support@openai.com">ads-support@openai.com</A>, or use the support widget in OpenAI&#39;s Help Center. OpenAI asks for:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>For zero delivery: the account name, campaign name, launch date and a screenshot showing zero delivery</Li>
        <Li>For a drop with unchanged settings: the campaign goal, the bid, your conversion setup and when the drop began</Li>
        <Li>For a rejected ad: the ad title and copy, the rejection message, the landing page URL and when you got the notice</Li>
      </ul>
      <P>
        If the problem spans several campaigns or accounts, say so and give a few concrete examples. Never include passwords or card details. For the full launch sequence these checks sit inside, see our guide to <A href="/blog/how-to-set-up-chatgpt-ad-campaign">setting up a ChatGPT ad campaign</A>, and for where everything lives in the interface, our <A href="/blog/how-to-use-chatgpt-ads-manager">Ads Manager walkthrough</A>. More on the channel itself is in our <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> glossary entry.
      </P>

      {/* ── HowTo + FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to fix a ChatGPT Ads campaign with no impressions",
            step: howToSteps.map(([name, text], i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name,
              text,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps (September 2026)</A></Li>
        <Li><A href="/blog/how-to-check-chatgpt-ads-pixel-is-working">How to Check Your ChatGPT Ads Pixel Is Working</A></Li>
        <Li><A href="/blog/chatgpt-ads-maximize-results-b2b">ChatGPT Ads Now Bids for You by Default. B2B Should Opt Out.</A></Li>
        <Li><A href="/blog/what-is-oai-adsbot">What Is OAI-AdsBot? (OpenAI&#39;s ChatGPT Ads Crawler)</A></Li>
        <Li><A href="/blog/how-to-use-chatgpt-ads-manager">How to Use ChatGPT Ads: A Marketer&#39;s Step-by-Step Guide (2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "chatgpt-ads-no-impressions",
  title: "ChatGPT Ads Getting No Impressions? How to Find and Fix the Cause",
  seoTitle: "ChatGPT Ads Getting No Impressions? How to Fix It",
  excerpt:
    "When ChatGPT Ads get zero impressions, something at one of four levels is blocking delivery, or the ad cannot win the auction. Here is how to find which.",
  metaDescription:
    "Why ChatGPT Ads get no impressions: what's normal in the first 24 hours, the four-level check, review and crawler blocks, bids, budgets and audience size.",
  date: "2026-10-26",
  readTime: "10 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "Ads Manager", "Troubleshooting", "Ad Delivery", "Ad Review", "OAI-AdsBot", "HowTo"],
  Content,
};

export default post;
